import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import _ from "lodash";
import TitleBar from "../_common/TitleBar";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { downloadItemEstimatedQuantitiesData } from "../../_actions/reportsActions";
import Badge from "../_common/Badge";

export default function ItemEstimateQuantitiesReport(props) {
  const { tab } = props;
  const dispatch = useDispatch();

  const report = useSelector((state) => state.reports.openReports[tab.tabId]);

  const [sortColumn, setSortColumn] = useState({ path: "itemNo", order: "asc" });

  const data = useMemo(() => {
    return _.orderBy(
      report.itemEstimateQuantitiesSummaryReportData ?? [],
      [sortColumn.path],
      [sortColumn.order]
    );
  }, [sortColumn, report]);

  const getQueryParamsSnippet = useCallback((qc) => {
    return qc && qc.length > 0 ? qc.join(", ") : "All Items";
  }, []);

  const handleClick_download = async (e) => {
    e.preventDefault();
    await dispatch(downloadItemEstimatedQuantitiesData(report.queryParams));
  };

  return (
    <React.Fragment>
      <TitleBar titleText={"Item Estimate Quantities Report"} />
      <div className="card mx-2">
        <div className="card-body">
          <h3>Item Estimate Quantities Report</h3>
          <div className="form-row form-group">
            <label className="col-sm-3 col-form-label">Report Created On:</label>
            <div className="col-sm-5">
              <input
                type="text"
                disabled
                className="form-control"
                value={moment(report.createdOn).format("MM/DD/YYYY HH:mm")}
              />
            </div>
          </div>
          <div className="form-row form-group">
            <label className="col-sm-3 col-form-label">Report Created By:</label>
            <div className="col-sm-5">
              <input
                type="text"
                disabled
                className="form-control"
                value={report.createdBy}
              />
            </div>
          </div>
          <div className="form-row form-group">
            <label className="col-sm-3 col-form-label">Remarks:</label>
            <div className="col-sm-5">
              <textarea className="form-control" rows={3} />
            </div>
          </div>
          <div className="form-row form-group">
            <label className="col-sm-3 col-form-label">Items searched:</label>
            <div className="col-sm-5">
              <textarea
                rows="5"
                disabled
                className="form-control"
                value={getQueryParamsSnippet(report.itemInfos)}
              />
            </div>
          </div>
          <div className="form-row form-group">
            <label className="col-sm-9" />
          </div>
          <div className="form-row form-group">
            <button className="btn btn-secondary" onClick={handleClick_download}>
              <FontAwesomeIcon icon={"download"} className="fa-fw mr-1" />
              Download CSV
            </button>
          </div>
          <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Item #</TableCell>
                  <TableCell align="left">Description</TableCell>
                  <TableCell align="right">EE Quantity</TableCell>
                  <TableCell align="right">Reported Quantity</TableCell>
                  <TableCell align="right">Bid Unit Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => (
                  <Row key={row.bidItemId} dataRow={row} />
                ))}
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    Grand Total This Report =
                  </TableCell>
                  <TableCell align="right">
                    {report.total.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </React.Fragment>
  );
}

const Row = ({ dataRow }) => {
  const [open, setOpen] = useState(false);

  const description = useMemo(() => {
    if (!dataRow.isFinalPay) return dataRow.description;
    return (
      <div className="d-flex flex-row">
        <div className="mr-1">{dataRow.description}</div>
        <span className="py-1 badge badge-light border">Final Pay</span>
      </div>
    );
  }, [dataRow]);

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            <FontAwesomeIcon icon={open ? "caret-down" : "caret-right"} />
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {dataRow.itemNo}
        </TableCell>
        <TableCell align="left">{description}</TableCell>
        <TableCell align="right">
          {dataRow.eeQuantity}&nbsp;{dataRow.unitOfMeasure}
        </TableCell>
        <TableCell align="right">
          {dataRow.quantity}&nbsp;{dataRow.unitOfMeasure}
        </TableCell>
        <TableCell align="right">
          {dataRow.bidUnitPrice.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </TableCell>
        <TableCell align="right">
          {dataRow.total.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </TableCell>
      </TableRow>
      <LocationBreakdown
        locationData={dataRow.locations}
        bidUnitPrice={dataRow.bidUnitPrice}
        unitOfMeasure={dataRow.unitOfMeasure}
        isOpen={open}
      />
    </React.Fragment>
  );
};

Row.propTypes = {
  dataRow: PropTypes.object.isRequired,
};

const LocationBreakdown = ({ locationData, bidUnitPrice, unitOfMeasure, isOpen }) => {
  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 1 }}>
            <Typography variant="h6" gutterBottom component="div">
              Breakdown by Location
            </Typography>
            <Table size="small" aria-label="adrs">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Location Name</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(locationData ?? []).map((loc) => (
                  <LocationBreakdownRow
                    location={loc}
                    unitOfMeasure={unitOfMeasure}
                    bidUnitPrice={bidUnitPrice}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const LocationBreakdownRow = ({ location: loc, unitOfMeasure, bidUnitPrice }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getLocationTotal = useCallback(
    (locQty) => {
      const total = locQty * bidUnitPrice;
      return total.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    [bidUnitPrice]
  );

  return (
    <>
      <TableRow key={loc.locationId}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setIsOpen(!isOpen)}
          >
            <FontAwesomeIcon icon={isOpen ? "caret-down" : "caret-right"} />
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {loc.name}
        </TableCell>
        <TableCell>
          {loc.quantity}&nbsp;{unitOfMeasure}
        </TableCell>
        <TableCell>{getLocationTotal(loc.quantity)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <div className="card my-2 bg-lightyellow">
              <div className="card-body p-3">
                <Typography variant="h6" gutterBottom component="div">
                  Breakdown by Report
                </Typography>
                <Table size="small" aria-label="adrs">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rpt Date</TableCell>
                      <TableCell>Rpt No.</TableCell>
                      <TableCell>Reported By</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>WorkDescription</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(loc.locationQuantityBreakdown ?? [])
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((adr) => (
                        <TableRow key={adr.assitantDailyReportId}>
                          <TableCell component="th" scope="row">
                            {new Date(adr.reportDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{adr.reportNo}</TableCell>
                          <TableCell>{adr.reportedBy}</TableCell>
                          <TableCell>
                            {adr.quantity}&nbsp;{unitOfMeasure}
                          </TableCell>
                          <TableCell>{adr.workDescription}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={(loc.locationQuantityBreakdown ?? []).length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
